import { withQueueLock } from "../browserApis";
import { drainDegradations } from "../degradations";
import { mountDevtools, unmountDevtools } from "../devtools";
import {
  buildEvent,
  type IAnalyticsEnrichers,
  type IAnalyticsEvent,
} from "../payload";
import { readQueue, writeQueue } from "../storage";
import { type IBtAnalyticsPlugin } from "./IBtAnalyticsPlugin";

/** First backoff step after a failed flush; doubles per consecutive failure. */
const BACKOFF_BASE_MS = 1_000;

/**
 * Ceiling on the backoff, matching the provider's default flush interval.
 *
 * Steady state during a sustained outage is one retry per *two* idle-timer
 * ticks, not one per tick: the provider's interval is phase-locked to mount
 * while `nextAttemptAt` is measured from the end of the last attempt, so once
 * the backoff saturates at the cap every other tick lands inside the window and
 * is suppressed. Getting there takes nine consecutive failures — the ramp
 * doubles from 1s, so it only exceeds a 300s tick at 2^8 = 256s → 512s.
 *
 * Either way the point holds: retries are paced by the timer rather than by
 * user interaction.
 */
const BACKOFF_MAX_MS = 300_000;

/**
 * Ceiling on a body sent with `keepalive`.
 *
 * The fetch spec gives keepalive requests a 64KiB budget shared across every
 * inflight keepalive request on the page, and a body over it rejects outright
 * instead of being queued. `frontendMaxPayloadSizeBytes` defaults to 500,000,
 * so a full queue is well past it — hence the per-flush size check below
 * rather than setting the flag unconditionally.
 */
const KEEPALIVE_MAX_BODY_BYTES = 64 * 1024;

/**
 * Whether `body` is small enough to send with `keepalive`.
 *
 * UTF-8 byte length is never below the UTF-16 code-unit count, so an
 * over-length string is over budget without encoding it — which keeps the
 * encode off the common path where the queue is near the 500KB cap.
 */
function fitsKeepaliveBudget(body: string): boolean {
  if (body.length > KEEPALIVE_MAX_BODY_BYTES) {
    return false;
  }
  return new TextEncoder().encode(body).length <= KEEPALIVE_MAX_BODY_BYTES;
}

/**
 * Whether the browser may cancel an inflight request out from under us.
 *
 * WebKit reports a cancelled fetch as `TypeError: Load failed` — byte-identical
 * to the one a dead endpoint produces, so the rejection carries no signal of
 * its own. The page being hidden is the only thing separating "the browser tore
 * our request down on the way out" from "the endpoint is unreachable".
 */
function isPageHidden(): boolean {
  return document.visibilityState === "hidden";
}

export interface ISawmillV3Config {
  frontendEnabled: boolean;
  ingestionEndpoint: string;
  frontendTenantId: string;
  frontendBatchThreshold: number;
  frontendMaxCachedEvents: number;
  frontendMaxPayloadSizeBytes: number;
  frontendFlushIntervalMs: number;
}

export interface ISawmillV3PluginOptions {
  config: ISawmillV3Config;
  enrichers: IAnalyticsEnrichers;
  /**
   * Where flush failures go. Required rather than optional-with-a-no-op
   * default: `track` is fire-and-forget, so a swallowed error is 100% silent
   * analytics loss with no signal that it happened. Tests that do not care can
   * pass `vi.fn()`.
   */
  reportError: (message: string) => void;
  /**
   * Where non-fatal diagnostics go — currently queue trims from the count and
   * byte caps, which are the only visibility into events being dropped.
   */
  reportMessage: (message: string) => void;
}

export interface ISawmillV3Plugin extends IBtAnalyticsPlugin {
  name: "sawmill-v3";
  config: ISawmillV3Config;
  flushIntervalMs: number;
  flushNow: () => Promise<void>;
  dispose: () => void;
  getQueueSnapshot: () => IAnalyticsEvent[];
}

export function sawmillV3Plugin(
  options: ISawmillV3PluginOptions,
): ISawmillV3Plugin {
  const { config, enrichers, reportError, reportMessage } = options;
  let disabled = false;

  const trimWhile = (
    queue: IAnalyticsEvent[],
    overCap: () => boolean,
    flavor: string,
  ): void => {
    let dropped = 0;
    while (overCap()) {
      queue.shift();
      dropped++;
    }
    if (dropped > 0) {
      reportMessage(`sawmill-v3 trimmed ${dropped} event(s) (${flavor})`);
    }
  };

  /**
   * UTF-8 byte length of one event, or `null` if it cannot be serialised.
   *
   * TextEncoder rather than `JSON.stringify(e).length`: the latter counts
   * UTF-16 code units, which undercounts the real payload by ~1.11x on a
   * CJK-heavy queue and up to 1.84x on pure CJK — so a 500KB cap would quietly
   * admit ~556KB for a builder using Japanese job names. v1 makes exactly that
   * approximation (`sawmillPlugin.ts`); this keeps its O(N) shape without
   * inheriting the inaccuracy.
   */
  const byteLengthOf = (event: IAnalyticsEvent): number | null => {
    try {
      return new TextEncoder().encode(JSON.stringify(event)).length;
    } catch {
      // Circular value or BigInt. Runs in `track` — fire-and-forget — and
      // ahead of writeQueue, so letting this escape would kill the event
      // before storage.ts gets the chance to drop just the offending one.
      return null;
    }
  };

  const enforceCaps = (queue: IAnalyticsEvent[]): void => {
    trimWhile(
      queue,
      () => queue.length > config.frontendMaxCachedEvents,
      `count cap ${config.frontendMaxCachedEvents}`,
    );

    // Size every event once, then trim against a running total. The previous
    // version re-serialised the whole queue on each iteration of the trim loop,
    // which is O(n^2): at default config a full queue is ~520KB against a 500KB
    // cap, so the trim fires at steady state and dropped ~61 events at a cost
    // of ~59ms of synchronous main-thread work — inside navigator.locks, on a
    // click. Sizing once is ~1.3ms for the same result.
    //
    // An unserialisable event contributes 0 rather than aborting the pass;
    // storage.ts drops it per-event moments later, and the count cap above
    // still bounds the queue meanwhile.
    const sizes = queue.map((event) => byteLengthOf(event) ?? 0);
    // +1 per event for the joining comma, +2 for the enclosing brackets.
    let total =
      sizes.reduce((sum, size) => sum + size + 1, 0) -
      (sizes.length > 0 ? 1 : 0) +
      2;

    let dropped = 0;
    // `> 1` so a single event larger than the whole cap is kept rather than
    // trimming to empty and storing it anyway — dropping it is storage.ts's
    // call, not the cap's.
    while (queue.length > 1 && total > config.frontendMaxPayloadSizeBytes) {
      total -= (sizes.shift() ?? 0) + 1;
      queue.shift();
      dropped++;
    }
    if (dropped > 0) {
      reportMessage(
        `sawmill-v3 trimmed ${dropped} event(s) (byte cap ${config.frontendMaxPayloadSizeBytes})`,
      );
    }
  };

  // In-memory backoff state. Resets on reload, which is acceptable for now: a
  // reload during an outage restarts the ramp rather than continuing it, but
  // persisting it would mean a second storage key to keep in sync with the
  // queue for a bounded amount of extra protection.
  let consecutiveFailures = 0;
  let nextAttemptAt = 0;

  const noteFlushFailure = (message: string): void => {
    // Report only the failure that actually hit the network. Without this a
    // sustained outage produces one Sentry entry per click.
    if (consecutiveFailures === 0) {
      reportError(message);
    }
    consecutiveFailures++;
    const delay = Math.min(
      BACKOFF_BASE_MS * 2 ** (consecutiveFailures - 1),
      BACKOFF_MAX_MS,
    );
    nextAttemptAt = Date.now() + delay;
  };

  /**
   * Surfaces any platform API the pipeline had to degrade away from.
   *
   * Reported from here rather than from the guards themselves: those run inside
   * `buildEvent` and the queue lock, neither of which can see the reporters, and
   * both run per event. Draining on flush reports each kind once, and a
   * degradation that starts mid-session still lands on the next flush.
   */
  const reportDegradations = (): void => {
    for (const kind of drainDegradations()) {
      reportMessage(`sawmill-v3 degraded: ${kind} unavailable`);
    }
  };

  const flush = async (events: IAnalyticsEvent[]): Promise<void> => {
    reportDegradations();

    // A failed flush re-queues its batch, and the queue is still at or above
    // the batch threshold — so without this guard the next event POSTs again
    // immediately, with a larger payload, against an endpoint that is already
    // unhealthy. Events keep queueing and capping normally while suppressed.
    if (consecutiveFailures > 0 && Date.now() < nextAttemptAt) {
      writeQueue(events);
      return;
    }

    const envelope = {
      tenantId: config.frontendTenantId,
      events,
    };
    const body = JSON.stringify(envelope);
    // Let the request outlive the page where the browser allows it, so a flush
    // racing a navigation completes rather than being torn down. Over the
    // 64KiB budget the flag would reject the request outright, so a large
    // batch goes without it and takes its chances, staying queued for the
    // next page.
    const keepalive = fitsKeepaliveBudget(body);
    // keepalive delivers the request, but not the continuation below: once
    // the page unloads, the success path never clears the queue and the next
    // page POSTs the same batch again. Ingestion has no idempotency (see the
    // `omits idempotencyKey` test), so that resend is a warehouse duplicate.
    // Dequeue up front instead and re-queue only on failure — trading a rare
    // loss (hidden, unloaded, *and* failed) for a near-certain duplicate.
    if (keepalive && isPageHidden()) {
      writeQueue([]);
    }
    try {
      const response = await window.fetch(config.ingestionEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body,
        keepalive,
      });
      if (response.ok) {
        if (consecutiveFailures > 0) {
          // Close the loop on the single failure report. Without this a
          // multi-hour outage and a blip both look like one Sentry entry, and
          // there is no way to tell "recovered" from "still down" without
          // querying the warehouse.
          reportMessage(
            `sawmill-v3 flush recovered after ${consecutiveFailures} consecutive failure(s)`,
          );
        }
        consecutiveFailures = 0;
        nextAttemptAt = 0;
        writeQueue([]);
        return;
      }
      writeQueue(events);
      noteFlushFailure(`sawmill-v3 flush failed: HTTP ${response.status}`);
    } catch (err) {
      const reason = err instanceof Error ? err.message : String(err);
      writeQueue(events);
      if (isPageHidden()) {
        // A rejection while the page is hidden is overwhelmingly the browser
        // cancelling our request on its way out, not an outage — and the
        // rejection itself cannot tell us which, since WebKit reports both as
        // `TypeError: Load failed`. Reporting it anyway produced ~124k Sentry
        // events from ~3.2k users, 100% WebKit and 89% on the two hard
        // navigations out of login, while the companion "recovered" message
        // fired on the very next flush.
        //
        // The events are already re-queued above — even when the batch was
        // dequeued before sending — so staying silent costs nothing: the next
        // flush sends them and reports for real if the endpoint is actually
        // down. Skipping the backoff too is deliberate —
        // a cancelled request never reached the endpoint, so there is nothing
        // to back off from, and the page is being torn down regardless.
        return;
      }
      noteFlushFailure(`sawmill-v3 flush failed: rejected: ${reason}`);
    }
  };

  const flushNow = async (): Promise<void> => {
    await withQueueLock("sawmillEventsV3", async () => {
      const queue = readQueue() as IAnalyticsEvent[];
      if (queue.length === 0) {
        return;
      }
      await flush(queue);
    });
  };

  const devtoolsDispatcher = {
    attemptFlushNow: flushNow,
    disable: () => {
      disabled = true;
    },
  };
  mountDevtools(devtoolsDispatcher);

  return {
    name: "sawmill-v3",
    config,
    flushIntervalMs: config.frontendFlushIntervalMs,
    track: async ({ payload }) => {
      if (!config.frontendEnabled || disabled) {
        return;
      }
      const { input, mergedContext } = payload.properties.__v3;
      const event = buildEvent(input, mergedContext, enrichers);
      if (
        import.meta.env.DEV &&
        window.localStorage.getItem("bt-debugSawmillV3") === "true"
      ) {
        // eslint-disable-next-line no-console -- opt-in dev debug; tree-shaken out of prod via import.meta.env.DEV
        console.log("[sawmill-v3] enqueue", event);
      }
      await withQueueLock("sawmillEventsV3", async () => {
        const queue = readQueue() as IAnalyticsEvent[];
        queue.push(event);
        enforceCaps(queue);
        if (queue.length >= config.frontendBatchThreshold) {
          await flush(queue);
        } else {
          writeQueue(queue);
        }
      });
    },
    flushNow,
    dispose: () => {
      disabled = true;
      // Identity-checked: React builds the replacement plugin before disposing
      // this one, so an unconditional teardown would delete the new instance's
      // devtools handle.
      unmountDevtools(devtoolsDispatcher);
    },
    getQueueSnapshot: () => readQueue() as IAnalyticsEvent[],
  };
}
